import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { HttpHandlingService } from 'src/app/api/services/http-handling.service';
import { SERVER_API_URL } from 'src/app/common/types/app.constants';
import {
  FullStandings,
  StandingItem,
  Standings,
} from 'src/app/common/types/standings.model';
import { Race } from '../../../types/cec.types';
import { MenuComponent } from 'src/app/controls/menu/components/menu.component';
import { ResultService } from './services/result.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-standings',
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.scss'],
  standalone: true,
  imports: [MenuComponent, TranslateModule],
  providers: [ResultService],
})
export class ResultsComponent implements OnInit {
  public races: Race[] = [new Race(), new Race(), new Race()];

  standings: Standings[] | null = [];
  seasonResults: StandingItem | null = null;
  speedTrialSeasonResults: StandingItem | null = null;

  @Output() onCloseSideNav = new EventEmitter<void>();

  /** Result service reference */
  protected service = inject(ResultService);

  constructor(
    private httpHandlingService: HttpHandlingService,
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit(): void {}

  onGetResults(): void {
    const competitionResultsDetailed =
      SERVER_API_URL + '/api/competition-results-detailed';
    this.http
      .get<FullStandings>(competitionResultsDetailed, {
        headers: this.httpHandlingService.authorizationHeaders(),
      })
      .subscribe(
        (result: FullStandings) => {
          this.standings = result.standings;
          this.seasonResults = result.seasonResults;
          this.speedTrialSeasonResults = result.speedTrialSeasonResults;
        },
        (error: HttpErrorResponse) => {
          this.handleRegistrationHttpErrors(error);
        }
      );
  }

  /**
   * function for stage results loading of desired stage in competition
   * @param stageId for evaluation of specific stage
   */
  onSelectStageResult(competitionId: number, stageId: number): void {
    this.onCloseSideNav.emit();
    this.router.navigate([
      '/competition',
      competitionId,
      'stage',
      stageId,
      'results',
    ]);
  }

  /**
   * function for stage results loading of desired stage in competition
   * @param stageId for evaluation of specific stage
   */
  onSelectStageSpeedTrialResult(stageId: number): void {
    this.onCloseSideNav.emit();
    this.router.navigate(['/stage', stageId, 'speed-trial', 'results']);
  }

  /**
   * function for round results loading of desired round in competition
   * @param roundId for evaluation of specific round
   */
  onSelectRoundResult(competitionId: number, roundId: number): void {
    this.onCloseSideNav.emit();
    this.router.navigate([
      '/competition',
      competitionId,
      'round',
      roundId,
      'results',
    ]);
  }

  /**
   * function for round results loading of desired round in competition
   * @param roundId for evaluation of specific round
   */
  onSelectRoundSpeedTrialResult(roundId: number): void {
    this.onCloseSideNav.emit();
    this.router.navigate(['/round', roundId, 'speed-trial', 'results']);
  }

  /**
   * function for navigation to competition results
   */
  onSelectCompetitionResult(competitionId: number): void {
    this.onCloseSideNav.emit();
    this.router.navigate(['/competition', competitionId, 'results']);
  }

  /**
   * function for round results loading of desired round in competition
   * @param roundId for evaluation of specific round
   */
  onSelectCompetitionSpeedTrialResult(competitionId: number): void {
    this.onCloseSideNav.emit();
    this.router.navigate([
      '/competition',
      competitionId,
      'speed-trial',
      'results',
    ]);
  }

  /**
   * function for navigation to season results
   */
  onSelectSeasonResult(seasonId: number, type: string): void {
    this.onCloseSideNav.emit();
    this.router.navigate(['/season', seasonId, type, 'results']);
  }

  /**
   * function for navigation to season speed trial results
   */
  onSelectSeasonSpeedTrialResult(seasonId: number, type: string): void {
    this.router.navigate(['/season', seasonId, type, 'speed-trial', 'results']);
  }

  /**
   * function for navigating to selected driving report
   * @param competitionId for evaluation of desired competition
   * @param stageId for evaluation of desired stage
   * @param crewId for evaluation of desired crew
   * @param drivingReportId for evaluation of desired driving report
   */
  onSelectDrivingReport(
    competitionId: number,
    stageId: number,
    crewId: number,
    drivingReportId: number
  ): void {
    this.onCloseSideNav.emit();
    this.router.navigate([
      '/competition',
      competitionId,
      'stage',
      stageId,
      'crew',
      crewId,
      'driving-report',
      drivingReportId,
    ]);
  }

  /**
   * function for handling HTTP errors in the process of receiving competitor
   */
  handleRegistrationHttpErrors(error: HttpErrorResponse): void {
    if (error.status === 0) {
      alert(error.message);
    }
  }

  closeSideNav(): void {
    this.onCloseSideNav.emit();
  }
}
